import React, { useState, useEffect } from 'react'
import {
  Users as UsersIcon,
  ChevronLeft,
  ChevronRight,
  Shield,
  User as UserIcon,
  Eye,
  Edit,
  UserPlus,
  CheckCircle,
  XCircle,
  Download,
  Trash2,
  Crown,
} from 'lucide-react'
import {
  useUsers,
  useDeleteUserPermanent,
  useBulkUpdateUsers,
  useBulkDeleteUsers,
} from '@/hooks/use-api'
import { usePagination } from '@/hooks/use-pagination'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { SearchWithRecent } from '@/components/ui/search-with-recent'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { formatDate } from '@/lib/utils'
import { getUserTypeDisplayName } from '@/lib/userTypeDisplay'
import { exportUsersToCSV } from '@/lib/csv-export'
import { CreateUserModal } from '@/components/CreateUserModal'
import { EditUserModal } from '@/components/EditUserModal'
import { UserDetailModal } from '@/components/UserDetailModal'
import { BulkActionsBar } from '@/components/BulkActionsBar'
import { BulkDeleteDialog } from '@/components/BulkDeleteDialog'
import { BulkUpdateModal, type BulkUpdateField } from '@/components/BulkUpdateModal'
import { useToast } from '@/hooks/use-toast'
import { useUser } from '@/contexts/UserContext'
import type { User as _User } from '@/types'
import { GRACKLE_COLORS, CATEGORICAL, SEMANTIC } from '@/lib/theme'

const Users: React.FC = () => {
  const { toast } = useToast()
  const { currentUser, startImpersonation, isAdmin, isSuperAdmin, impersonatedUser } = useUser()

  // Only Admin/Super Admin can export (and not when impersonating a non-admin)
  const canExport =
    isAdmin && (!impersonatedUser || ['Admin', 'Super Admin'].includes(impersonatedUser.user_type))
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const { page, firstPage, prevPage, nextPage, extract } = usePagination()
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [viewUserId, setViewUserId] = useState<string | null>(null)
  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState<(typeof users)[0] | null>(null)
  const deleteUserPermanent = useDeleteUserPermanent()
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false)
  const [bulkUpdateModalOpen, setBulkUpdateModalOpen] = useState(false)
  const bulkUpdateMutation = useBulkUpdateUsers()
  const bulkDeleteMutation = useBulkDeleteUsers()

  // Clear selection when search, filter, or page changes
  useEffect(() => {
    setSelectedIds(new Set())
  }, [search, filter, page])

  // Use the useUsers hook with pagination
  // Note: filter needs to be implemented in the backend API
  const { data } = useUsers({
    search,
    page,
    limit: 25,
  })

  const {
    items: allUsers,
    total: totalFromAPI,
    currentPage,
    limit,
    totalPages: totalPagesFromAPI,
    hasNextPage,
    hasPrevPage,
  } = extract(data)

  // Apply client-side filtering until backend supports filter param
  const users =
    filter === 'all'
      ? allUsers
      : filter === 'active'
        ? allUsers.filter((u) => u.is_active)
        : allUsers.filter((u) => !u.is_active)

  const total = filter === 'all' ? totalFromAPI : users.length
  const totalPages = filter === 'all' ? totalPagesFromAPI : Math.ceil(total / limit)

  const handleFilterChange = (newFilter: 'all' | 'active' | 'inactive') => {
    setFilter(newFilter)
    firstPage()
  }

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleToggleSelectAll = () => {
    if (selectedIds.size === users.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(users.map((u) => u.id)))
    }
  }

  const userUpdateFields: BulkUpdateField[] = [
    {
      key: 'user_type',
      label: 'Role',
      type: 'select',
      options: [
        { value: 'User', label: 'User' },
        { value: 'Admin', label: 'Admin' },
        { value: 'Super Admin', label: 'Super Admin' },
        { value: 'Support', label: 'Support' },
      ],
    },
    { key: 'is_active', label: 'Active', type: 'boolean' },
  ]

  const handleEditUser = (userId: string) => {
    setSelectedUserId(userId)
    setEditModalOpen(true)
  }

  const handleCreateUser = () => {
    setCreateModalOpen(true)
  }

  const handleImpersonateUser = async (user: (typeof users)[0]) => {
    // Safety checks
    // Cannot impersonate Super Admin
    if (user.user_type === 'Super Admin') {
      toast({
        title: 'Cannot impersonate',
        description: 'Cannot impersonate Super Admin users.',
        variant: 'destructive',
      })
      return
    }

    // Only Super Admin can impersonate Admin
    if (user.user_type === 'Admin' && !isSuperAdmin) {
      toast({
        title: 'Cannot impersonate',
        description: 'Only Super Admin can impersonate Admin users.',
        variant: 'destructive',
      })
      return
    }

    // Cannot impersonate inactive users
    if (!user.is_active) {
      toast({
        title: 'Cannot impersonate',
        description: 'Cannot impersonate inactive users.',
        variant: 'destructive',
      })
      return
    }

    try {
      await startImpersonation(user.id)
      toast({
        title: 'Impersonation started',
        description: `You are now viewing the CRM as ${user.name}`,
      })
      window.location.reload()
    } catch (error) {
      toast({
        title: 'Failed to start impersonation',
        description: 'An error occurred. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleExportCSV = () => {
    // Export all users (filtered if filter is active)
    exportUsersToCSV(users)
    toast({
      title: 'Export Successful',
      description: `Exported ${users.length} users to CSV`,
    })
  }

  // Check if a user can be permanently deleted
  // Note: Super Admin can delete inactive Admin users (handled by backend)
  const canDeleteUser = (user: (typeof users)[0]) =>
    !user.is_active &&
    user.user_type !== 'Super Admin' &&
    // Only Super Admin can delete Admin users
    (user.user_type !== 'Admin' || isSuperAdmin) &&
    Number(user.accounts_owned ?? 0) === 0 &&
    Number(user.opportunities_owned ?? 0) === 0 &&
    Number(user.tasks_owned ?? 0) === 0

  const handleDeleteUserClick = (user: (typeof users)[0]) => {
    setUserToDelete(user)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!userToDelete) return

    try {
      await deleteUserPermanent.mutateAsync(userToDelete.id)
      toast({
        title: 'User Deleted',
        description: `${userToDelete.name} has been permanently deleted.`,
      })
    } catch (error: any) {
      toast({
        title: 'Delete Failed',
        description: error.response?.data?.message || 'Failed to delete user.',
        variant: 'destructive',
      })
    } finally {
      setDeleteDialogOpen(false)
      setUserToDelete(null)
    }
  }

  // Role-tier icon colours mirror the canonical role→family mapping used by
  // UserDetailModal so the two admin screens agree (Super Admin→Prussian,
  // Admin→Navy, everyone else→Teal).
  const getUserTypeIcon = (userType: string) => {
    if (userType === 'Super Admin') {
      return <Crown className="w-4 h-4" style={{ color: CATEGORICAL.prussian.solid }} />
    }
    if (userType === 'Admin') {
      return <Shield className="w-4 h-4" style={{ color: CATEGORICAL.navy.solid }} />
    }
    return <UserIcon className="w-4 h-4" style={{ color: CATEGORICAL.teal.solid }} />
  }

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <Badge
        style={{
          backgroundColor: SEMANTIC.success.fill,
          color: SEMANTIC.success.text,
          borderColor: SEMANTIC.success.fill,
        }}
      >
        <CheckCircle className="w-3 h-3 mr-1" aria-hidden="true" />
        Active
      </Badge>
    ) : (
      <Badge
        style={{
          backgroundColor: SEMANTIC.neutral.fill,
          color: SEMANTIC.neutral.text,
          borderColor: SEMANTIC.neutral.fill,
        }}
      >
        <XCircle className="w-3 h-3 mr-1" aria-hidden="true" />
        Inactive
      </Badge>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium border bg-gray-100 text-gray-700 border-gray-300">
              <UsersIcon className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Users</span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight">Users</h1>
          </div>
          <p className="text-muted-foreground mt-1">Manage system users and their permissions</p>
        </div>
        <div className="flex gap-2">
          {canExport && (
            <Button variant="outline" aria-label="Export users to CSV" onClick={handleExportCSV}>
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              Export
            </Button>
          )}
          <Button aria-label="Create new user" onClick={handleCreateUser}>
            <UserPlus className="mr-2 h-4 w-4" aria-hidden="true" />
            New User
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-2 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <UsersIcon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{total}</div>
            <p className="text-xs text-muted-foreground leading-tight">System users</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Active Users</CardTitle>
            <CheckCircle className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">
              {users.filter((u) => u.is_active).length}
            </div>
            <p className="text-xs text-muted-foreground leading-tight">Can access the system</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Admin Users</CardTitle>
            <Shield className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">
              {users.filter((u) => u.user_type === 'Admin' || u.user_type === 'Super Admin').length}
            </div>
            <p className="text-xs text-muted-foreground leading-tight">Full system access</p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-3">
          <div className="flex flex-col lg:flex-row gap-4">
            <SearchWithRecent
              value={search}
              onChange={(v) => {
                setSearch(v)
                firstPage()
              }}
              placeholder="Search by name, email, or username..."
              ariaLabel="Search users"
              entityKey="users"
              className="flex-1"
            />
            <div className="flex gap-2">
              <Button
                variant={filter === 'all' ? 'default' : 'outline'}
                onClick={() => handleFilterChange('all')}
                size="sm"
              >
                All ({total})
              </Button>
              <Button
                variant={filter === 'active' ? 'default' : 'outline'}
                onClick={() => handleFilterChange('active')}
                size="sm"
              >
                Active
              </Button>
              <Button
                variant={filter === 'inactive' ? 'default' : 'outline'}
                onClick={() => handleFilterChange('inactive')}
                size="sm"
              >
                Inactive
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {isAdmin && (
                  <th className="px-4 py-3 w-10">
                    <Checkbox
                      checked={selectedIds.size === users.length && users.length > 0}
                      onCheckedChange={handleToggleSelectAll}
                      aria-label={
                        selectedIds.size === users.length
                          ? 'Deselect all users'
                          : 'Select all users'
                      }
                    />
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Owned Records
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-muted">
                  {isAdmin && (
                    <td className="px-4 py-4 w-10">
                      <Checkbox
                        checked={selectedIds.has(user.id)}
                        onCheckedChange={() => handleToggleSelect(user.id)}
                        aria-label={`Select ${user.name}`}
                      />
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      {isAdmin ? (
                        <button
                          className="text-sm font-medium text-gray-900 hover:underline focus:underline focus:outline-none text-left"
                          onClick={() => {
                            setViewUserId(user.id)
                            setViewModalOpen(true)
                          }}
                          aria-label={`View details for ${user.name}`}
                        >
                          {user.name}
                        </button>
                      ) : (
                        <span className="text-sm font-medium text-gray-900">{user.name}</span>
                      )}
                      <div className="text-sm text-gray-500">{user.email}</div>
                      {user.username && (
                        <div className="text-xs text-gray-400">@{user.username}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(user.is_active)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getUserTypeIcon(user.user_type)}
                      <span className="text-sm text-gray-900">
                        {getUserTypeDisplayName(user.user_type)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {(user.accounts_owned ?? 0) > 0 && (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                          style={{
                            backgroundColor: CATEGORICAL.prussian.tint,
                            color: GRACKLE_COLORS.prussianBlue,
                          }}
                        >
                          {user.accounts_owned} accounts
                        </span>
                      )}
                      {(user.opportunities_owned ?? 0) > 0 && (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                          style={{
                            backgroundColor: CATEGORICAL.evergreen.tint,
                            color: GRACKLE_COLORS.prussianBlue,
                          }}
                        >
                          {user.opportunities_owned} opps
                        </span>
                      )}
                      {(user.tasks_owned ?? 0) > 0 && (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                          style={{
                            backgroundColor: CATEGORICAL.navy.tint,
                            color: GRACKLE_COLORS.prussianBlue,
                          }}
                        >
                          {user.tasks_owned} tasks
                        </span>
                      )}
                      {(user.accounts_owned ?? 0) === 0 &&
                        (user.opportunities_owned ?? 0) === 0 &&
                        (user.tasks_owned ?? 0) === 0 && (
                          <span className="text-xs text-gray-400">No records</span>
                        )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.last_login ? formatDate(user.last_login) : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditUser(user.id)}
                        style={{ color: GRACKLE_COLORS.navy }}
                        className="hover:opacity-70"
                        aria-label={`Edit ${user.name}`}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {isAdmin &&
                        user.user_type !== 'Super Admin' &&
                        user.is_active &&
                        user.id !== currentUser?.id &&
                        // Admin can only impersonate User, Super Admin can impersonate Admin and User
                        (isSuperAdmin || user.user_type !== 'Admin') && (
                          <button
                            onClick={() => handleImpersonateUser(user)}
                            style={{ color: CATEGORICAL.clay.solid }}
                            className="hover:opacity-70"
                            aria-label={`View CRM as ${user.name}`}
                            title="View as this user"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                      {isAdmin && canDeleteUser(user) && (
                        <button
                          onClick={() => handleDeleteUserClick(user)}
                          style={{ color: GRACKLE_COLORS.darkRaspberry }}
                          className="hover:opacity-70"
                          aria-label={`Permanently delete ${user.name}`}
                          title="Permanently delete user"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-700">
                  Showing page <span className="font-medium">{currentPage}</span> of{' '}
                  <span className="font-medium">{totalPages}</span> ({total} total users)
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrevPage}>
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNextPage}>
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateUserModal open={createModalOpen} onOpenChange={setCreateModalOpen} />
      <EditUserModal userId={selectedUserId} open={editModalOpen} onOpenChange={setEditModalOpen} />
      <UserDetailModal userId={viewUserId} open={viewModalOpen} onOpenChange={setViewModalOpen} />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete <strong>{userToDelete?.name}</strong> (
              {userToDelete?.email})? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              style={{
                backgroundColor: GRACKLE_COLORS.darkRaspberry,
                color: '#FFFFFF',
              }}
              className="hover:opacity-90"
            >
              {deleteUserPermanent.isPending ? 'Deleting...' : 'Delete Permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Operations */}
      {isAdmin && (
        <>
          <BulkActionsBar
            selectedCount={selectedIds.size}
            entityType="user"
            onBulkDelete={() => setBulkDeleteDialogOpen(true)}
            onBulkUpdate={() => setBulkUpdateModalOpen(true)}
            onClearSelection={() => setSelectedIds(new Set())}
            isDeleting={bulkDeleteMutation.isPending}
            isUpdating={bulkUpdateMutation.isPending}
          />
          <BulkDeleteDialog
            open={bulkDeleteDialogOpen}
            onOpenChange={setBulkDeleteDialogOpen}
            selectedCount={selectedIds.size}
            entityType="user"
            onConfirm={async () => {
              await bulkDeleteMutation.mutateAsync(Array.from(selectedIds))
              toast({ title: 'Success', description: `${selectedIds.size} user(s) deactivated.` })
              setSelectedIds(new Set())
              setBulkDeleteDialogOpen(false)
            }}
            isDeleting={bulkDeleteMutation.isPending}
          />
          <BulkUpdateModal
            open={bulkUpdateModalOpen}
            onOpenChange={setBulkUpdateModalOpen}
            selectedCount={selectedIds.size}
            entityType="user"
            fields={userUpdateFields}
            onConfirm={async (updates) => {
              await bulkUpdateMutation.mutateAsync({ ids: Array.from(selectedIds), updates })
              toast({ title: 'Success', description: `${selectedIds.size} user(s) updated.` })
              setSelectedIds(new Set())
              setBulkUpdateModalOpen(false)
            }}
            isUpdating={bulkUpdateMutation.isPending}
          />
        </>
      )}
    </div>
  )
}

export default Users
